// KOA-5857 — Undo / redo for the table editor's pending state.
//
// Wraps the existing pendingChangesReducer with a JSON-Patch-backed
// history stack so every user-visible mutation (cell edits, AI fill
// batches, row inserts, row deletes) can be undone / redone with a
// keyboard shortcut or toolbar button.
//
// Why patches over full snapshots: a typical editing session touches
// dozens of cells across hundreds of rows, but each individual action
// changes only a handful of bytes (one cell, one row insert). Storing
// 50 snapshots of an IPendingChanges with 100 dirty cells each adds up
// quickly; storing 50 patches stays constant-tiny. fast-json-patch is
// already in the project's dependency set so this is no new runtime
// cost.
//
// Why not direct inverse-action recording (command pattern): the
// reducer's no-op-revert collapse and the "drop the whole row update
// when no dirty columns remain" branches mean an action's effect isn't
// 1:1 with its forward representation. Computing the actual state
// delta after the reducer runs avoids re-implementing those decisions
// in inverse form.

import * as jsonpatch from "fast-json-patch";
import {
  emptyPendingChanges,
  IPendingChanges,
  IPendingRowInsert,
  IPendingRowUpdate,
  PendingChangesAction,
  pendingChangesReducer,
  RowPkString
} from "./pending-changes-reducer";

// JSON-friendly mirror of IPendingChanges. Maps and Sets are not native
// JSON; converting Map → keyed object and Set → array is the smallest
// transformation that lets fast-json-patch operate on the state.
interface ISerializablePending {
  updates: { [pk: string]: IPendingRowUpdate };
  inserts: IPendingRowInsert[];
  deletes: string[];
}

const serializePending = (p: IPendingChanges): ISerializablePending => {
  const updates: { [pk: string]: IPendingRowUpdate } = {};
  p.updates.forEach((value: IPendingRowUpdate, key: RowPkString): void => {
    updates[key] = value;
  });
  return {
    updates,
    inserts: p.inserts,
    deletes: Array.from(p.deletes)
  };
};

const deserializePending = (s: ISerializablePending): IPendingChanges => {
  const updates: Map<RowPkString, IPendingRowUpdate> = new Map();
  Object.entries(s.updates).forEach(([key, value]: [string, IPendingRowUpdate]): void => {
    updates.set(key, value);
  });
  return {
    updates,
    inserts: s.inserts,
    deletes: new Set<RowPkString>(s.deletes)
  };
};

// One reversible step in the history stack. `forward` advances from the
// pre-action state to the post-action state; `inverse` rolls it back.
// Both are pre-computed at dispatch time so undo / redo are pure patch
// applications with no reducer re-execution.
//
// Coalescing metadata lives on the entry so consecutive edits to the
// same cell within the COALESCE_WINDOW_MS window can REPLACE the top
// entry with a recomputed patch from the original session-start state
// to the latest state, instead of pushing one history step per
// keystroke. Without this, typing "Alice" into a cell would create
// 5 separate undo steps and the user would have to Cmd+Z five times
// to clear it.
//   * coalesceKey identifies which cell the session is currently
//     editing (`cell:<pk>:<column>` for persisted rows, `insert:
//     <clientId>:<column>` for pending inserts). null for non-edit
//     actions (insert / delete / undelete) which never coalesce.
//   * coalescePreState is the serialised pre-edit state captured the
//     first time the session opened, so a recomputed patch can span
//     the whole session rather than just the latest delta.
//   * timestamp drives the time-window check.
export interface IPendingHistoryEntry {
  forward: jsonpatch.Operation[];
  inverse: jsonpatch.Operation[];
  coalesceKey: string | null;
  coalescePreState: ISerializablePending | null;
  timestamp: number;
}

const diffPatches = (
  prevJson: ISerializablePending,
  nextJson: ISerializablePending
): { forward: jsonpatch.Operation[]; inverse: jsonpatch.Operation[] } => {
  return {
    forward: jsonpatch.compare(prevJson, nextJson),
    inverse: jsonpatch.compare(nextJson, prevJson)
  };
};

const coalesceKeyForAction = (action: PendingChangesAction): string | null => {
  if (action.type === "edit-cell") {
    return `cell:${action.pk}:${action.column}`;
  }
  if (action.type === "edit-insert-cell") {
    return `insert:${action.clientId}:${action.column}`;
  }
  return null;
};

// Time window for keystroke coalescing. ~1 second is the sweet spot:
// long enough that backspaces and re-types feel like one edit, short
// enough that the user pausing to think reads as a deliberate boundary.
// Same magnitude as VS Code / Google Docs / Notion.
const COALESCE_WINDOW_MS: number = 1000;

const applyPatch = (state: IPendingChanges, patch: jsonpatch.Operation[]): IPendingChanges => {
  if (patch.length === 0) {
    return state;
  }
  const json: ISerializablePending = serializePending(state);
  // mutate=false so the input isn't touched; validate=false because we
  // produced the patches ourselves and validating each op against the
  // schema is wasted work at dispatch hot-path.
  const result = jsonpatch.applyPatch(json, patch, false, false);
  return deserializePending(result.newDocument as ISerializablePending);
};

// Stack cap. 50 actions covers a few minutes of intensive editing on
// the largest realistic table and keeps memory bounded.
const HISTORY_CAP: number = 50;

export interface IPendingHistoryState {
  state: IPendingChanges;
  past: IPendingHistoryEntry[];
  future: IPendingHistoryEntry[];
  // True when the top of `past` is still open for keystroke coalescing.
  // Cleared by undo, redo, clear-history, end-coalesce-session, and any
  // non-edit reducer action (insert / delete / clear-resolved / reset).
  // Cleared so a Cmd+Z followed by more typing doesn't accidentally
  // extend the just-undone entry's session.
  coalesceActive: boolean;
}

export const emptyHistoryState = (): IPendingHistoryState => {
  return {
    state: emptyPendingChanges(),
    past: [],
    future: [],
    coalesceActive: false
  };
};

export type HistoryAwareAction =
  // Applies a reducer action and records the resulting patch on the
  // history stack. Clears the redo (future) stack since the new branch
  // diverges from any prior redo path.
  | { type: "apply"; action: PendingChangesAction }
  // Replays the inverse of the top past entry; moves the entry from
  // past → future so it can be redone.
  | { type: "undo" }
  // Replays the forward of the top future entry; moves the entry from
  // future → past.
  | { type: "redo" }
  // Clears both stacks. Called after a save (the resolved batch can't
  // be undone — the rows are persisted) and after a discard.
  | { type: "clear-history" }
  // Closes the current keystroke-coalescing session so the next edit
  // creates a fresh entry even if it targets the same cell within the
  // time window. Fired on input blur, before bulk operations like AI
  // fill, and whenever the editor wants to mark a hard boundary.
  | { type: "end-coalesce-session" };

export const pendingHistoryReducer = (
  state: IPendingHistoryState,
  action: HistoryAwareAction
): IPendingHistoryState => {
  switch (action.type) {
    case "apply": {
      const next: IPendingChanges = pendingChangesReducer(state.state, action.action);
      if (next === state.state) {
        // Reducer bailed (no-op revert, etc.) — nothing to record.
        return state;
      }
      const key: string | null = coalesceKeyForAction(action.action);
      const top: IPendingHistoryEntry | undefined = state.past[state.past.length - 1];
      const now: number = Date.now();
      const canCoalesce: boolean =
        state.coalesceActive &&
        key !== null &&
        top !== undefined &&
        top.coalesceKey === key &&
        top.coalescePreState !== null &&
        now - top.timestamp < COALESCE_WINDOW_MS;
      if (canCoalesce && top !== undefined && top.coalescePreState !== null) {
        // Same cell, still within the session window — REPLACE the top
        // entry with a patch from the session-start state to the latest
        // state. Net effect: one undo step covers the whole typing
        // session, including backspaces and re-types.
        const preJson: ISerializablePending = top.coalescePreState;
        const nextJson: ISerializablePending = serializePending(next);
        const patches = diffPatches(preJson, nextJson);
        if (patches.forward.length === 0) {
          // User typed then reverted to the session-start state. Drop
          // the top entry entirely — no history record needed.
          return {
            state: next,
            past: state.past.slice(0, -1),
            future: [],
            coalesceActive: false
          };
        }
        const replaced: IPendingHistoryEntry = {
          forward: patches.forward,
          inverse: patches.inverse,
          coalesceKey: key,
          coalescePreState: preJson,
          timestamp: now
        };
        return {
          state: next,
          past: [...state.past.slice(0, -1), replaced],
          future: [],
          coalesceActive: true
        };
      }
      // New session (or non-coalescable action). Snapshot the pre-state
      // when the action is a cell edit so a follow-up keystroke can
      // coalesce; for non-edit actions, leave coalescePreState null and
      // mark the session as closed.
      const preJson: ISerializablePending = serializePending(state.state);
      const nextJson: ISerializablePending = serializePending(next);
      const patches = diffPatches(preJson, nextJson);
      if (patches.forward.length === 0) {
        return state;
      }
      const fresh: IPendingHistoryEntry = {
        forward: patches.forward,
        inverse: patches.inverse,
        coalesceKey: key,
        coalescePreState: key !== null ? preJson : null,
        timestamp: now
      };
      const past: IPendingHistoryEntry[] = [...state.past, fresh].slice(-HISTORY_CAP);
      return {
        state: next,
        past,
        future: [],
        coalesceActive: key !== null
      };
    }
    case "undo": {
      if (state.past.length === 0) {
        return state;
      }
      const entry: IPendingHistoryEntry = state.past[state.past.length - 1]!;
      const newState: IPendingChanges = applyPatch(state.state, entry.inverse);
      return {
        state: newState,
        past: state.past.slice(0, -1),
        future: [...state.future, entry],
        coalesceActive: false
      };
    }
    case "redo": {
      if (state.future.length === 0) {
        return state;
      }
      const entry: IPendingHistoryEntry = state.future[state.future.length - 1]!;
      const newState: IPendingChanges = applyPatch(state.state, entry.forward);
      return {
        state: newState,
        past: [...state.past, entry],
        future: state.future.slice(0, -1),
        coalesceActive: false
      };
    }
    case "clear-history": {
      return { state: state.state, past: [], future: [], coalesceActive: false };
    }
    case "end-coalesce-session": {
      if (!state.coalesceActive) {
        return state;
      }
      return { ...state, coalesceActive: false };
    }
  }
};
